import {
  InvoiceStatus,
  PayerType,
  TransactionStatus,
  TransactionType,
} from "elephant-web/gql";
import {
  type CalculatedInvoice,
  type InvoiceBalance,
  type InvoiceLineSource,
  type InvoicePayerReference,
  type InvoicePayerSource,
  type InvoiceSource,
  type InvoiceTransaction,
  InvoiceValidationError,
} from "./invoice-source";

export const copyInvoiceData = <T>(value: T): T => {
  if (Array.isArray(value)) return value.map(copyInvoiceData) as T;
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [
        key,
        copyInvoiceData(child),
      ]),
    ) as T;
  }
  return value;
};

export const invoiceMinorUnits = (value: number): number => {
  if (!Number.isSafeInteger(value) || value < 0) {
    throw new InvoiceValidationError(
      "Money must be non-negative safe integer minor units",
    );
  }
  return value;
};

export const invoiceLineTotal = (
  line: Pick<InvoiceLineSource, "cost" | "quantity" | "disabled">,
): number => {
  invoiceMinorUnits(line.cost);
  if (!Number.isSafeInteger(line.quantity) || line.quantity <= 0) {
    throw new InvoiceValidationError(
      "Line quantity must be a positive safe integer",
    );
  }
  return line.disabled ? 0 : invoiceMinorUnits(line.cost * line.quantity);
};

export const findInvoicePayer = (
  payers: InvoicePayerSource[],
  reference: InvoicePayerReference,
): number => {
  const matches = payers.flatMap((payer, index) => {
    if (reference.type === PayerType.Patient) {
      return payer.type === PayerType.Patient ? [index] : [];
    }
    if (payer.type !== PayerType.Insurer) return [];
    return reference.id
      ? payer.id === reference.id
        ? [index]
        : []
      : payer.name === reference.name
        ? [index]
        : [];
  });
  if (matches.length !== 1) {
    throw new InvoiceValidationError(
      "Payer linkage must identify exactly one invoice payer",
    );
  }
  return matches[0];
};

const successfulTransactionPayer = (
  source: InvoiceSource,
  transaction: InvoiceTransaction,
): number => {
  if (transaction.type === TransactionType.ChargeInsurer) {
    if (transaction.payerType !== PayerType.Insurer || !transaction.payerName) {
      throw new InvoiceValidationError(
        "Insurer charge requires declared payer linkage",
      );
    }
    return findInvoicePayer(source.payers, {
      type: PayerType.Insurer,
      name: transaction.payerName,
      ...(transaction.payerId ? { id: transaction.payerId } : {}),
    });
  }
  const index = findInvoicePayer(source.payers, { type: PayerType.Patient });
  if (transaction.payerId && source.payers[index].id !== transaction.payerId) {
    throw new InvoiceValidationError(
      "Patient transaction payer ID does not match the patient",
    );
  }
  return index;
};

const validateUniqueIds = (rows: { id: string }[], label: string): void => {
  const ids = rows.map((row) => row.id);
  if (ids.some((id) => !id) || new Set(ids).size !== ids.length) {
    throw new InvoiceValidationError(`${label} IDs must be present and unique`);
  }
};

export const calculateInvoice = (input: InvoiceSource): CalculatedInvoice => {
  const source = copyInvoiceData(input);
  const { currency, phase, lineItems, payers, transactions, ...metadata } =
    source;
  validateUniqueIds(lineItems, "Line item");
  validateUniqueIds(transactions, "Transaction");
  findInvoicePayer(payers, { type: PayerType.Patient });
  const insurerNames = payers
    .filter((payer) => payer.type === PayerType.Insurer)
    .map((payer) => payer.name);
  if (new Set(insurerNames).size !== insurerNames.length) {
    throw new InvoiceValidationError(
      "Insurer names must be unique for declared wire linkage",
    );
  }
  const payerIds = payers.flatMap((payer) =>
    payer.id ? [{ id: payer.id }] : [],
  );
  validateUniqueIds(payerIds, "Payer");
  if (
    payers.some(
      (payer) =>
        payer.type !== PayerType.Patient && payer.type !== PayerType.Insurer,
    )
  ) {
    throw new InvoiceValidationError(
      "Invoice payer roster must contain only patient and insurers",
    );
  }
  const emptyBalance = (): InvoiceBalance => ({
    currency,
    total: 0,
    due: 0,
    paid: 0,
    waived: 0,
    exempt: 0,
  });
  const calculatedPayers = payers.map((payer) => ({
    ...payer,
    balance: emptyBalance(),
    claimedLineItems: [] as { lineItemId: string; amountInPence: number }[],
  }));
  for (const line of lineItems) {
    const payer = calculatedPayers[findInvoicePayer(payers, line.payer)];
    const total = invoiceLineTotal(line);
    payer.balance.total = invoiceMinorUnits(payer.balance.total + total);
    if (payer.type === PayerType.Insurer) {
      payer.claimedLineItems.push({
        lineItemId: line.id,
        amountInPence: total,
      });
    }
  }
  let refunded = 0;
  let hasVoid = false;
  for (const transaction of transactions) {
    if (transaction.status !== TransactionStatus.Success) continue;
    if (transaction.type === TransactionType.Void) {
      hasVoid = true;
      continue;
    }
    if (!transaction.amount || transaction.amount.currency !== currency) {
      throw new InvoiceValidationError(
        "Successful financial transaction requires invoice-currency amount",
      );
    }
    const amount = transaction.amount.value;
    invoiceMinorUnits(
      transaction.type === TransactionType.Refund ? -amount : amount,
    );
    const balance =
      calculatedPayers[successfulTransactionPayer(source, transaction)].balance;
    switch (transaction.type) {
      case TransactionType.Payment:
      case TransactionType.ChargeInsurer:
        balance.paid += amount;
        break;
      case TransactionType.Refund:
        refunded = invoiceMinorUnits(refunded - amount);
        balance.paid += amount;
        break;
      case TransactionType.Waiver:
        balance.waived = invoiceMinorUnits(balance.waived + amount);
        break;
      case TransactionType.Exempt:
        balance.exempt = invoiceMinorUnits(balance.exempt + amount);
        break;
      default:
        throw new InvoiceValidationError(
          "Unsupported financial transaction type",
        );
    }
  }
  if (
    (phase === "DRAFT" || phase === "PENDING") &&
    transactions.some(
      (transaction) => transaction.status === TransactionStatus.Success,
    )
  ) {
    throw new InvoiceValidationError(
      "Unfinalised invoices cannot contain successful financial actions",
    );
  }
  if (phase === "REFUNDED" && refunded === 0) {
    throw new InvoiceValidationError(
      "Refunded lifecycle requires a successful refund",
    );
  }
  if (phase === "VOID" && !hasVoid) {
    throw new InvoiceValidationError(
      "Void lifecycle requires a successful void action",
    );
  }
  const closed = phase === "VOID" || phase === "REFUNDED";
  for (const payer of calculatedPayers) {
    const { total, paid, waived, exempt } = payer.balance;
    invoiceMinorUnits(paid);
    const remaining = invoiceMinorUnits(total - paid - waived - exempt);
    payer.balance.due = closed ? 0 : remaining;
  }
  const balance = calculatedPayers.reduce(
    (total, payer) => ({
      currency,
      total: invoiceMinorUnits(total.total + payer.balance.total),
      due: invoiceMinorUnits(total.due + payer.balance.due),
      paid: invoiceMinorUnits(total.paid + payer.balance.paid),
      waived: invoiceMinorUnits(total.waived + payer.balance.waived),
      exempt: invoiceMinorUnits(total.exempt + payer.balance.exempt),
    }),
    emptyBalance(),
  );
  const patient =
    calculatedPayers[findInvoicePayer(payers, { type: PayerType.Patient })];
  const insurerPaid = calculatedPayers.some(
    (payer) => payer.type === PayerType.Insurer && payer.balance.paid > 0,
  );
  const invoiceStatus =
    phase === "DRAFT"
      ? InvoiceStatus.Draft
      : phase === "PENDING"
        ? InvoiceStatus.Pending
        : phase === "VOID"
          ? InvoiceStatus.Void
          : phase === "REFUNDED"
            ? patient.balance.paid === 0 && !insurerPaid
              ? InvoiceStatus.Refunded
              : InvoiceStatus.PartiallyRefunded
            : balance.due === 0
              ? InvoiceStatus.Paid
              : balance.paid + balance.waived + balance.exempt > 0
                ? InvoiceStatus.PartiallyPaid
                : InvoiceStatus.Unpaid;
  return {
    ...metadata,
    invoiceStatus,
    balance,
    lineItems: lineItems.map(({ payer: _payer, ...line }) => line),
    payers: calculatedPayers,
    transactions,
  };
};
